import React, { useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import { EnrollmentUpdateRow, RosterRow, ScheduleRosterDTO, TargetSchedule, UPDATE_TYPE, UpdateOptionsDTO } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import BuscadorHorarios, { DetalleHorario, FiltroCasillas, HorarioFila, filaDeDestino } from "../../../../components/horarios/buscadorHorarios";
import { useVeWappChat } from "../../../../components/whatsapp/whatsappAlumno";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";
import S from "./strings";
import { ACADEMIC_APPLY_TYPES, TabProps, TYPE_LABELS, typeChips, dinero, errorText, fechaCorta, nombreHorario, sessionDelta, tipoDe } from "./helpers";
import { ConfirmarAplicar, Fallo, PersonaConfirmar } from "./piezas";
import FusionarHorario from "./fusionarHorario";
import { HistorialLista } from "./historialTab";
import { conPeriodo, etiquetaHorario } from "../../../../utils/etiquetaHorario";
import EstadoCuentaModal from "../../../../components/estadoCuenta/estadoCuentaModal";
import { useCapacidades } from "./apiBase";
import T from "./textosBase";

interface Props extends TabProps { initialScheduleId?: number; onActualizar: (studentId: number, enrollmentId: number) => void; }

// Lo que se puede hacer con varias alumnas a la vez; Cambio a Otro Curso y Reingreso son de una en una.
const MOVER: number[] = [UPDATE_TYPE.SCHEDULE_CHANGE, UPDATE_TYPE.NEXT_BLOCK, UPDATE_TYPE.POSTPONED, UPDATE_TYPE.PAUSED, UPDATE_TYPE.NO_SCHEDULE, UPDATE_TYPE.RETIRED];

const Modal: React.FC<{ titulo: string; onClose: () => void; children: React.ReactNode }> = ({ titulo, onClose, children }) => (
	<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={onClose}>
		<div className="wau-modal ancho" onClick={(e) => e.stopPropagation()}>
			<h3>{titulo}</h3>
			{children}
			<div className="wau-acciones"><button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>{S.cancel}</button></div>
		</div>
	</div>
);

/** Actualizar, Un Horario: busca un horario, ve a sus alumnas y las actualiza una por una o en lote. */
const UnHorario: React.FC<Props> = ({ campusId, countryId, movil, canApplyNow, notify, changed, initialScheduleId, onActualizar }) => {
	const http = HttpManager.getInstance();
	const veWa = useVeWappChat();
	const [scheduleId, setScheduleId] = useState<number | null>(initialScheduleId ?? null);
	const [fila, setFila] = useState<HorarioFila | null>(null);
	const [buscando, setBuscando] = useState<boolean>(!initialScheduleId);
	const [roster, setRoster] = useState<ScheduleRosterDTO | null>(null);
	const [log, setLog] = useState<EnrollmentUpdateRow[]>([]);
	const [options, setOptions] = useState<UpdateOptionsDTO | null>(null);
	const [seg, setSeg] = useState<"alumnas" | "historial">("alumnas");
	const [pagoFiltro, setPagoFiltro] = useState<string[]>([]);
	const [asistFiltro, setAsistFiltro] = useState<string[]>([]);
	const [selected, setSelected] = useState<number[]>([]);
	const [typeId, setTypeId] = useState<number | null>(null);
	const [destino, setDestino] = useState<HorarioFila | null>(null);
	const [buscandoDestino, setBuscandoDestino] = useState<boolean>(true);
	const [notes, setNotes] = useState<{ [enrollmentId: number]: string }>({});
	const [modal, setModal] = useState<"close" | "merge" | "mentor" | null>(null);
	const [mentors, setMentors] = useState<{ id: number; name: string; enabled: boolean; openSchedules: number }[] | null>(null);
	const [mentorFiltro, setMentorFiltro] = useState<string>("");
	const [masivo, setMasivo] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	// «Aplicar N» nunca aplica de un clic: primero se confirma, por persona, qué va a pasar.
	const [confirmando, setConfirmando] = useState<boolean>(false);
	const [tick, setTick] = useState<number>(0);
	// Lo que no se pudo cargar se dice con su motivo y «Reintentar» (antes: «Cargando…» sin fin).
	const [fallo, setFallo] = useState<string | null>(null);
	const [falloOpciones, setFalloOpciones] = useState<string | null>(null);
	const [falloMaestras, setFalloMaestras] = useState<string | null>(null);
	const [cuenta, setCuenta] = useState<RosterRow | null>(null);
	const cap = useCapacidades(campusId);

	useEffect(() => {
		if (!scheduleId) { setRoster(null); setFallo(null); return; }
		let vigente = true;
		setRoster(null); setFallo(null); setFalloOpciones(null); setLog([]); setOptions(null); setSelected([]); setTypeId(null); setDestino(null); setBuscandoDestino(true); setNotes({});
		Promise.all([
			http.enrollmentUpdatesService.scheduleRoster(campusId, scheduleId),
			http.enrollmentUpdatesService.log(campusId, { scheduleId }).then((r) => r.data.data).catch(() => [] as EnrollmentUpdateRow[])
		]).then(([r, l]) => {
			if (!vigente) { return; }
			setRoster(r.data); setLog(l);
			// Los destinos y los tipos son los mismos para todo el grupo (mismo curso): se piden con la primera alumna libre.
			const primera = r.data.rows.find((x) => !x.pendingUpdate || x.pendingUpdate.typeId === null) || r.data.rows[0];
			if (primera) { http.enrollmentUpdatesService.options(campusId, primera.enrollmentId).then((o) => vigente && setOptions(o.data)).catch((e) => vigente && setFalloOpciones(errorText(e))); }
		}).catch((e) => vigente && setFallo(errorText(e)));
		return () => { vigente = false; };
	}, [campusId, scheduleId, tick]);

	const targets: TargetSchedule[] = useMemo(() => options?.targetSchedules || [], [options]);
	const soloIds = useMemo(() => targets.map((t) => t.id), [targets]);
	const complemento = useMemo(() => targets.map((t) => filaDeDestino(t, cursoCorto(roster?.schedule.courseName), campusId)), [targets, roster, campusId]);
	const detalle = useMemo(() => {
		const out: { [id: number]: DetalleHorario } = {};
		targets.forEach((t) => { out[t.id] = { cupo: t.limit || null, alumnos: t.enrolled, lleno: t.isFull, sesion: t.classesGiven + 1 }; });
		return out;
	}, [targets]);
	const target = targets.find((t) => t.id === destino?.id);

	const sch = roster?.schedule;
	const rows = roster?.rows || [];
	const libre = (r: RosterRow) => !r.pendingUpdate || r.pendingUpdate.typeId === null;
	const ultima = useMemo(() => {
		const mapa = new Map<number, EnrollmentUpdateRow>();
		log.forEach((u) => { if (u.statusId === 3 && !mapa.has(u.enrollmentId)) { mapa.set(u.enrollmentId, u); } });
		return mapa;
	}, [log]);
	const porUbicar = rows.filter((r) => r.pendingUpdate && r.pendingUpdate.typeId === null).length;

	const vistas = rows.filter((r) => {
		if (pagoFiltro.length && !pagoFiltro.includes(r.overdue > 0 ? "vencida" : "corriente")) { return false; }
		if (asistFiltro.length && !asistFiltro.includes(r.alerts.some((a) => a.code === "below_minimum") ? "baja" : "normal")) { return false; }
		return true;
	});
	const personas = rows.filter((r) => selected.includes(r.enrollmentId));
	// Los tipos del lote: el servidor dice cuáles aplican (Aplazar pide 4 sesiones dadas); los que no, deshabilitados con su motivo.
	const tiposLote = typeChips(options?.types, MOVER);
	const allowedTypes = tiposLote.map((t) => t.id);
	const bloqueo = (id: number): string | null => { const t = tiposLote.find((x) => x.id === id); return t && !t.allowed ? (t.reason || TYPE_LABELS[id]) : null; };
	const necesitaDestino = typeId === UPDATE_TYPE.SCHEDULE_CHANGE || typeId === UPDATE_TYPE.NEXT_BLOCK;
	const missing = personas.find((p) => !(notes[p.enrollmentId] || "").trim());
	const anyMore = !!target && personas.some((p) => sessionDelta(p.sessionsTaken, target.classesGiven).more > 0);
	const listo = personas.length > 0 && !!typeId && (!necesitaDestino || !!destino) && !missing && !busy;
	const podriaAplicar = listo && canApplyNow && (cap.isCaja || (cap.isAcademic && ACADEMIC_APPLY_TYPES.includes(typeId || 0)));
	const puedeAplicar = podriaAplicar && !anyMore;
	// «Aplicar N» is not offered when someone repeats classes (each one needs its own choice): say why instead of hiding it silently.
	const masClases = target ? Math.max(0, ...personas.map((p) => sessionDelta(p.sessionsTaken, target.classesGiven).more)) : 0;
	const porQueNoAplica = podriaAplicar && anyMore ? (cap.isCaja ? S.repiteDesdePorAplicar(masClases) : S.repiteRevisaCaja(masClases)) : null;

	// Lo que «Aplicar N» va a hacer con cada alumna marcada (mismo modal que Por Aplicar).
	const personaConfirmar = (p: RosterRow): PersonaConfirmar => {
		const t = typeId || 0;
		const tipo = tipoDe({ typeId: t, fromSchedule: { periodName: sch?.periodName }, toSchedule: { periodName: necesitaDestino ? destino?.periodo : null } });
		const lineas: string[] = [];
		if (S.estadoQueda[t]) { lineas.push(S.quedaEstado(S.estadoQueda[t])); }
		if (necesitaDestino && destino) { lineas.push(S.pasaA([destino.horario, destino.periodo].filter(Boolean).join(", "))); }
		const d = necesitaDestino && target ? sessionDelta(p.sessionsTaken, target.classesGiven) : { less: 0, more: 0 };
		if (d.more > 0) { lineas.push(S.repiteClases(d.more)); }
		if (d.less > 0) { lineas.push(S.faltanClases(d.less)); }
		if (p.overdue > 0) { lineas.push(S.debeVencidos(dinero(p.overdue, countryId))); }
		if (tipo === "Cambio de Periodo") { lineas.push(S.mismosMeses); }
		return { nombre: p.studentName, curso: cursoCorto(sch?.courseName) || sch?.courseName || "", tipo, lineas };
	};

	const recargar = () => setTick((t) => t + 1);
	const termina = (msg: string) => { notify(msg); setModal(null); recargar(); changed(); };
	const act = (fn: () => Promise<any>, msg: string) => {
		setBusy(true);
		fn().then(() => termina(msg)).catch((e) => notify(errorText(e), false)).finally(() => setBusy(false));
	};

	const mover = async (applyNow: boolean) => {
		if (!typeId) { return; }
		setBusy(true);
		let hechas = 0; let aplicadas = 0;
		try {
			// Las que ya traen un borrador del cierre del horario se definen sobre ese borrador; las demás se crean.
			for (const p of personas.filter((x) => x.pendingUpdate && x.pendingUpdate.typeId === null)) {
				const d = await http.enrollmentUpdatesService.setType(campusId, p.pendingUpdate!.id, { typeId, toScheduleId: necesitaDestino ? destino!.id : null, note: notes[p.enrollmentId].trim() });
				if (applyNow && d.data.canApply && !d.data.needsCashier && !(d.data.previewNow?.sessionsMore)) { await http.enrollmentUpdatesService.apply(campusId, p.pendingUpdate!.id, {}); aplicadas++; }
				hechas++;
			}
			const nuevas = personas.filter((x) => !x.pendingUpdate);
			if (nuevas.length) {
				const creada = await http.enrollmentUpdatesService.create(campusId, { applyNow, scheduleId: scheduleId, label: `${cursoCorto(sch?.courseName)}, ${fila?.horario || nombreHorario(sch?.name)}`,
					items: nuevas.map((p) => ({ enrollmentId: p.enrollmentId, typeId, note: notes[p.enrollmentId].trim(), toScheduleId: necesitaDestino ? destino!.id : null })) });
				hechas += nuevas.length; if (applyNow) { aplicadas += (creada.data.updates || []).filter((u) => u.statusId === 3).length; }
			}
			notify(applyNow && aplicadas === hechas ? S.appliedN(hechas) : S.sent(hechas));
			changed(); recargar();
		} catch (e) {
			notify(errorText(e), false);
			if (hechas) { changed(); recargar(); }
		} finally { setBusy(false); }
	};

	const abrirModal = (m: "close" | "merge" | "mentor") => {
		setModal(m);
		if (m === "mentor" && !mentors) { cargarMaestras(); }
	};

	const cargarMaestras = () => {
		setFalloMaestras(null);
		http.enrollmentUpdatesService.mentors(campusId).then((r) => setMentors(Array.isArray(r.data) ? r.data : [])).catch((e) => setFalloMaestras(errorText(e)));
	};

	// Verde solo para Activo / Matriculado: «Al Corriente» va neutro.
	const pagoPill = (r: RosterRow) => r.overdue > 0
		? <Badge bg="oro">{S.payOverdue(dinero(r.overdue, countryId))}</Badge> : <Badge bg="secondary">{S.payOk}</Badge>;
	const mes = (titulo: string | null | undefined) => (titulo || "").replace(/^Mensualidad\s+/i, "");
	// Saldo: pendiente (vencido en oro) y lo pagado con «hasta [mes]».
	const saldoCelda = (r: RosterRow) => (
		<div>
			<b className={r.overdue > 0 ? "wau-oro" : ""}>{dinero(r.debt, countryId)}</b>
			<div className={"small " + (r.overdue > 0 ? "wau-oro" : "wau-sec")}>{r.overdue > 0 ? `${T.vencido} ${dinero(r.overdue, countryId)}` : T.alCorriente}</div>
		</div>
	);
	const pagadoCelda = (r: RosterRow) => (
		<div>{dinero(r.paid || 0, countryId)}<div className="wau-sec small">{T.pagadoHasta} {r.paidThrough ? mes(r.paidThrough) : T.nada}</div></div>
	);
	const botonCuenta = (r: RosterRow) => <button type="button" className="btn btn-link btn-sm" style={{ whiteSpace: "nowrap" }} onClick={() => setCuenta(r)}>{T.estadoCuenta}</button>;
	const botonActualizar = (r: RosterRow) => <button type="button" className="btn btn-outline-secondary btn-sm" style={{ whiteSpace: "nowrap" }} onClick={() => onActualizar(r.studentId, r.enrollmentId)}>{S.update}</button>;
	const asistencia = (r: RosterRow) => r.attendancePct == null ? "—" : `${Math.round(r.attendancePct)} %`;
	const avance = (r: RosterRow) => r.sessionsTotal ? `${r.sessionsTaken} / ${r.sessionsTotal}` : String(r.sessionsTaken);
	const ultimaTexto = (r: RosterRow) => {
		if (r.pendingUpdate && r.pendingUpdate.typeId !== null) { return <Badge bg="info">{S.waiting}</Badge>; }
		if (r.lastUpdate) { return <span className="wau-sec">{TYPE_LABELS[r.lastUpdate.typeId || 0] || r.lastUpdate.typeLabel}, {fechaCorta(r.lastUpdate.appliedAt)}</span>; }
		const u = ultima.get(r.enrollmentId);
		return u ? <span className="wau-sec">{TYPE_LABELS[u.typeId || 0] || u.typeLabel}, {fechaCorta(u.eventAt || u.appliedAt)}</span> : <span className="wau-sec">—</span>;
	};
	const toggle = (r: RosterRow) => { if (libre(r)) { setSelected(selected.includes(r.enrollmentId) ? selected.filter((x) => x !== r.enrollmentId) : [...selected, r.enrollmentId]); } };
	const libres = vistas.filter(libre);
	const todas = libres.length > 0 && libres.every((r) => selected.includes(r.enrollmentId));
	const mentorLista = (mentors || []).filter((m) => m.enabled && (!mentorFiltro || m.name.toLowerCase().includes(mentorFiltro.toLowerCase())));
	const estadoPill = sch ? <Badge bg={sch.statusId === 4 ? "oro" : "secondary"} style={{ fontWeight: 500 }}>{sch.statusLabel}</Badge> : null;

	return (
		<div className="wau-pila">
			<div className="wau-caja wau-pila">
				{scheduleId && !buscando ? (
					<div className="wau-destino">
						<span><b>{(fila ? conPeriodo(fila.horario, fila.periodo) : etiquetaHorario(sch?.name, sch?.periodName)) || "…"}</b>{sch ? ", " + (sch.levelName || cursoCorto(sch.courseName)) : ""}</span>
						<button type="button" className="btn btn-link btn-sm" style={{ whiteSpace: "nowrap" }} onClick={() => setBuscando(true)}>{S.buscarOtro}</button>
					</div>
				) : (
					<BuscadorHorarios campusId={campusId} seleccionado={scheduleId} placeholder={S.pickSchedule} onElegir={(h) => { setScheduleId(h.id); setFila(h); setBuscando(false); setSeg("alumnas"); }} />
				)}
			</div>

			{scheduleId && fallo ? <Fallo texto={fallo} onReintentar={recargar} /> : scheduleId && !roster ? <div className="wau-caja wau-sec">{S.loading}</div> : null}

			{sch ? (
				<div className="wau-caja wau-pila" style={{ borderColor: "var(--wa-acento)" }}>
					<div className="wau-fila" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
						<div style={{ minWidth: 0, flex: "1 1 280px" }}>
							<h2 className="wau-h2">{fila?.horario || nombreHorario(sch.name)}, {sch.levelName || cursoCorto(sch.courseName)}</h2>
							<p className="wau-sec">{[sch.mentorName, sch.periodName, sch.startDate && sch.endDate ? `${fechaCorta(sch.startDate)} a ${fechaCorta(sch.endDate)}` : "", S.byStudent(rows.length)].filter(Boolean).join(", ")} {estadoPill}</p>
						</div>
						<div className="wau-fila">
							{sch.statusId === 1 || sch.statusId === 4 ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => abrirModal("mentor")}>{S.changeMentor}</button> : null}
							{sch.statusId === 1 || sch.statusId === 4 || sch.statusId === 0 ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => abrirModal("merge")}>{S.merge}</button> : null}
							{sch.statusId === 1 || sch.statusId === 4 ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => abrirModal("close")}>{S.closeSchedule}</button> : null}
							{veWa && rows.length > 0 ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setMasivo(true)}>{S.whatsappVarias}</button> : null}
						</div>
					</div>

					<div className="wau-fila" style={{ alignItems: "center" }}>
						<div className="waFmSegs" role="group">
							<button type="button" className={"waFmSeg" + (seg === "alumnas" ? " activo" : "")} aria-pressed={seg === "alumnas"} onClick={() => setSeg("alumnas")}>{S.students}<span className="wau-seg-n wau-num">{rows.length}</span></button>
							<button type="button" className={"waFmSeg" + (seg === "historial" ? " activo" : "")} aria-pressed={seg === "historial"} onClick={() => setSeg("historial")}>{S.historyTab}</button>
						</div>
						{porUbicar > 0 ? <Badge bg="oro">{S.toPlace(porUbicar)}</Badge> : null}
						{seg === "alumnas" ? (
							<div className="wau-chips" style={{ marginLeft: "auto", marginBottom: 0 }}>
								<FiltroCasillas etiqueta={S.col.pay} opciones={[{ v: "corriente", t: S.payOk }, { v: "vencida", t: "Vencida" }]} elegidos={pagoFiltro} onCambio={setPagoFiltro} />
								<FiltroCasillas etiqueta={S.col.attendance} opciones={[{ v: "normal", t: "Normal" }, { v: "baja", t: "Bajo el Mínimo" }]} elegidos={asistFiltro} onCambio={setAsistFiltro} />
							</div>
						) : null}
					</div>

					{seg === "historial" ? <HistorialLista campusId={campusId} countryId={countryId} rows={log} movil={movil} onChanged={(m) => notify(m)} onError={(m) => notify(m, false)} /> : vistas.length === 0 ? <div className="wau-vacio">{S.empty}</div> : movil ? (
						<div>
							{vistas.map((r) => (
								<div key={"rc" + r.enrollmentId} className="wau-tarjeta" style={{ borderColor: selected.includes(r.enrollmentId) ? "var(--wa-acento)" : undefined }}>
									<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
										<input type="checkbox" checked={selected.includes(r.enrollmentId)} disabled={!libre(r)} onChange={() => toggle(r)} aria-label={r.studentName} />
										<b style={{ flex: 1 }}>{r.studentName}</b>{pagoPill(r)}
									</div>
									<div className="linea wau-num">{T.pendiente} {dinero(r.debt, countryId)}, {T.pagado} {dinero(r.paid || 0, countryId)}, {T.pagadoHasta} {r.paidThrough ? mes(r.paidThrough) : T.nada}</div>
									<div className="linea wau-num">{avance(r)}, {asistencia(r)}</div>
									<div className="linea">{ultimaTexto(r)}</div>
									<div className="wau-fila" style={{ marginTop: 6, gap: 6, alignItems: "center" }}>{botonActualizar(r)}{botonCuenta(r)}</div>
								</div>
							))}
						</div>
					) : (
						<div className="wau-tabla">
							<table>
								<thead><tr><th style={{ width: 1 }}><input type="checkbox" checked={todas} onChange={() => setSelected(todas ? [] : libres.map((r) => r.enrollmentId))} aria-label={S.selectAll} /></th>
									<th>{S.col.student}</th><th>{T.saldo}</th><th>{T.pagado}</th><th>{S.col.progress}</th><th>{S.col.attendance}</th><th>{S.col.lastUpdate}</th><th /></tr></thead>
								<tbody className="wau-num">
									{vistas.map((r) => (
										<tr key={"r" + r.enrollmentId} className={selected.includes(r.enrollmentId) ? "sel" : ""}>
											<td><input type="checkbox" checked={selected.includes(r.enrollmentId)} disabled={!libre(r)} onChange={() => toggle(r)} aria-label={r.studentName} /></td>
											<td><b>{r.studentName}</b></td>
											<td style={{ whiteSpace: "nowrap" }}>{saldoCelda(r)}</td>
											<td style={{ whiteSpace: "nowrap" }}>{pagadoCelda(r)}</td>
											<td>{avance(r)}</td>
											<td className={r.alerts.some((a) => a.code === "below_minimum") ? "wau-oro" : ""}>{asistencia(r)}</td>
											<td>{ultimaTexto(r)}</td>
											<td className="der"><div className="wau-fila" style={{ gap: 4, flexWrap: "nowrap", justifyContent: "flex-end", alignItems: "center" }}>{botonCuenta(r)}{botonActualizar(r)}</div></td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			) : null}

			{sch && personas.length > 0 ? (
				<div className="wau-caja wau-pila">
					<div className="wau-fila" style={{ justifyContent: "space-between", alignItems: "center" }}><h3 style={{ margin: 0 }}>{S.moveStudents}</h3><Badge bg="secondary" style={{ fontWeight: 500, whiteSpace: "normal", textAlign: "left" }}>{fila ? conPeriodo(fila.horario, fila.periodo) : etiquetaHorario(sch.name, sch.periodName)}{sch.levelName ? ", " + sch.levelName : ""}</Badge></div>
					{falloOpciones ? <Fallo dentro texto={falloOpciones} onReintentar={recargar} /> : !options ? <span className="wau-sec">{S.loading}</span> : null}
					<div className="wau-chips" role="group" style={{ marginBottom: 0 }}>
						{allowedTypes.map((id) => (
							<button key={"mv" + id} type="button" className={"waFmBoton" + (typeId === id ? " activo" : "")} aria-pressed={typeId === id} aria-disabled={!!bloqueo(id)} title={bloqueo(id) || undefined} style={bloqueo(id) ? { opacity: .5 } : undefined}
								onClick={() => { const no = bloqueo(id); if (no) { notify(no, false); return; } setTypeId(id); setDestino(null); setBuscandoDestino(true); }}>{TYPE_LABELS[id]}</button>
						))}
					</div>
					{necesitaDestino ? (
						<div className="wau-pila">
							<span className="wau-lab">{S.horarioDestino}</span>
							{destino && !buscandoDestino ? (
								<div className="wau-destino"><span><b>{destino.horario}</b>{destino.periodo ? ", " + destino.periodo : ""}{destino.modulo ? ", " + destino.modulo : ""}{destino.maestro ? ", " + destino.maestro : ""}</span>
									<button type="button" className="btn btn-link btn-sm" onClick={() => setBuscandoDestino(true)}>{S.cambiar}</button></div>
							) : (
								<BuscadorHorarios compacto campusId={campusId} soloIds={soloIds} complemento={complemento} detalle={detalle} seleccionado={destino?.id}
									onElegir={(h) => { setDestino(h); setBuscandoDestino(false); }} />
							)}
						</div>
					) : null}
					{typeId ? (
						<>
							{movil ? personas.map((p) => (
								<div key={"mp" + p.enrollmentId} className="wau-tarjeta">
									<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><b>{p.studentName}</b>{pagoPill(p)}</div>
									<div className="linea wau-num">{avance(p)}</div>
									<input className="wau-entrada" style={{ marginTop: 6 }} placeholder={S.notePlaceholder} aria-label={S.note + " " + p.studentName} value={notes[p.enrollmentId] || ""}
										onChange={(e) => setNotes({ ...notes, [p.enrollmentId]: e.target.value })} />
								</div>
							)) : (
								<div className="wau-tabla">
									<table>
										<thead><tr><th>{S.col.student}</th><th>{S.col.pay}</th><th>{S.col.progress}</th>{necesitaDestino ? <th>{S.col.destination}</th> : null}<th>{S.col.note}</th></tr></thead>
										<tbody className="wau-num">
											{personas.map((p) => (
												<tr key={"mp" + p.enrollmentId}>
													<td><b>{p.studentName}</b></td><td>{pagoPill(p)}</td><td>{avance(p)}</td>
													{necesitaDestino ? <td>{destino ? conPeriodo(destino.horario, destino.periodo) : "—"}</td> : null}
													<td><input className="wau-entrada" placeholder={S.notePlaceholder} aria-label={S.note + " " + p.studentName} value={notes[p.enrollmentId] || ""}
														onChange={(e) => setNotes({ ...notes, [p.enrollmentId]: e.target.value })} /></td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
							{target && anyMore ? <div className="wau-nota"><span>{porQueNoAplica || S.repiteClases(masClases)}</span></div> : null}
							<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
								{puedeAplicar ? <button type="button" className="btn btn-primary btn-sm rounded-pill px-3" disabled={busy} onClick={() => setConfirmando(true)}>{S.applyN(personas.length)}</button> : null}
								<button type="button" className={"btn btn-sm " + (puedeAplicar ? "btn-outline-secondary" : "btn-primary rounded-pill px-3")} disabled={!listo} onClick={() => mover(false)}>{S.leavePending}</button>
								{missing ? <span className="wau-oro" style={{ fontSize: 13.5 }}>{S.missingNote(missing.studentName.split(" ")[0])}</span> : null}
							</div>
						</>
					) : null}
				</div>
			) : null}

			{confirmando && puedeAplicar ? <ConfirmarAplicar personas={personas.map(personaConfirmar)} busy={busy} onCancel={() => setConfirmando(false)} onConfirm={() => { setConfirmando(false); mover(true); }} /> : null}
			{cuenta ? <EstadoCuentaModal campusId={campusId} studentId={cuenta.studentId} studentName={cuenta.studentName} enrollmentId={cuenta.enrollmentId} onClose={() => setCuenta(null)} /> : null}
			{masivo && scheduleId && roster ? <EnvioMasivo campusId={campusId} studentIds={roster.rows.map((r) => r.studentId)} origen="horario" referencia={roster.schedule.name || null} onClose={() => setMasivo(false)} /> : null}

			{(modal === "merge" || modal === "close") && roster ? <FusionarHorario campusId={campusId} countryId={countryId} movil={movil} mode={modal} roster={roster} targets={targets} onClose={() => setModal(null)} onDone={termina} /> : null}
			{modal === "mentor" && scheduleId ? (
				<Modal titulo={S.changeMentor} onClose={() => setModal(null)}>
					<div className="wau-buscar"><input value={mentorFiltro} placeholder={S.newMentor} aria-label={S.newMentor} autoFocus onChange={(e) => setMentorFiltro(e.target.value)} /></div>
					<div className="wau-resultados" style={{ marginTop: 8 }}>
						{falloMaestras ? <div style={{ padding: 10 }}><Fallo dentro texto={falloMaestras} onReintentar={cargarMaestras} /></div> : mentors === null ? <span className="wau-sec" style={{ padding: 10 }}>{S.loading}</span> : mentorLista.length === 0 ? <span className="wau-sec" style={{ padding: 10 }}>{S.empty}</span> : mentorLista.map((m) => (
							<button key={"me" + m.id} type="button" disabled={busy} onClick={() => act(() => http.enrollmentUpdatesService.changeMentor(campusId, scheduleId, m.id), S.mentorOk)}>
								<b>{m.name}</b><span className="wau-sec">{m.openSchedules}</span>
							</button>
						))}
					</div>
				</Modal>
			) : null}
		</div>
	);
};

export default UnHorario;
